import { useCallback, type ChangeEvent } from 'react';
import { trackInteraction, INTERACTION_TYPES } from '../../../shared/observability';
import { AUDIT_PAGES } from '../tracking';
import type { AuditListFilters } from './useAuditListFilters';

type SetFilter = <K extends keyof Omit<AuditListFilters, 'keyword'>>(
  key: K,
  value: AuditListFilters[K],
) => void;

type FilterChangeValue = string | number | (string | number)[] | undefined;

export interface UseAuditFilterHandlersResult {
  handleKeywordChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleStatusChange: (value: FilterChangeValue) => void;
  handleAuditTypeChange: (value: FilterChangeValue) => void;
  handleLocationChange: (value: FilterChangeValue) => void;
  handleTemplateChange: (value: FilterChangeValue) => void;
  handleDivisionChange: (value: FilterChangeValue) => void;
  handleDateRangeTypeChange: (value: FilterChangeValue) => void;
}

interface UseAuditFilterHandlersParams {
  setFilter: SetFilter;
  setKeyword: (value: string) => void;
}

/**
 * Provides memoized onChange handlers for each audit list filter field.
 * Extracted from AuditListFilterBar to keep the component under 200 lines.
 */
export function useAuditFilterHandlers({
  setFilter,
  setKeyword,
}: UseAuditFilterHandlersParams): UseAuditFilterHandlersResult {
  const handleKeywordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setKeyword(e.target.value);
    },
    [setKeyword],
  );

  const handleStatusChange = useCallback(
    (value: FilterChangeValue) => {
      const arrayValue = Array.isArray(value)
        ? value.map(String)
        : value
          ? [String(value)]
          : [];
      setFilter('statuses', arrayValue);
      trackInteraction({
        type: INTERACTION_TYPES.filterUsed,
        page: AUDIT_PAGES.list,
        filterKey: 'statuses',
        valueCount: arrayValue.length,
      });
    },
    [setFilter],
  );

  const handleAuditTypeChange = useCallback(
    (value: FilterChangeValue) => {
      const arrayValue = Array.isArray(value)
        ? value.map(String)
        : value
          ? [String(value)]
          : [];
      setFilter('auditTypes', arrayValue);
      trackInteraction({
        type: INTERACTION_TYPES.filterUsed,
        page: AUDIT_PAGES.list,
        filterKey: 'auditTypes',
        valueCount: arrayValue.length,
      });
    },
    [setFilter],
  );

  const handleLocationChange = useCallback(
    (value: FilterChangeValue) => {
      const arrayValue = Array.isArray(value)
        ? value.map(String)
        : value
          ? [String(value)]
          : [];
      setFilter('locations', arrayValue);
      trackInteraction({
        type: INTERACTION_TYPES.filterUsed,
        page: AUDIT_PAGES.list,
        filterKey: 'locations',
        valueCount: arrayValue.length,
      });
    },
    [setFilter],
  );

  const handleTemplateChange = useCallback(
    (value: FilterChangeValue) => {
      const arrayValue = Array.isArray(value)
        ? value.map(String)
        : value
          ? [String(value)]
          : [];
      setFilter('templateIds', arrayValue);
    },
    [setFilter],
  );

  const handleDivisionChange = useCallback(
    (value: FilterChangeValue) => {
      setFilter('divisionName', value ? String(value) : '');
    },
    [setFilter],
  );

  const handleDateRangeTypeChange = useCallback(
    (value: FilterChangeValue) => {
      const type = value ? String(value) : '';
      if (!type) {
        setFilter('dateRange', null);
      } else {
        setFilter('dateRange', { type });
      }
      trackInteraction({
        type: INTERACTION_TYPES.filterUsed,
        page: AUDIT_PAGES.list,
        filterKey: 'dateRange',
        valueCount: type ? 1 : 0,
      });
    },
    [setFilter],
  );

  return {
    handleKeywordChange,
    handleStatusChange,
    handleAuditTypeChange,
    handleLocationChange,
    handleTemplateChange,
    handleDivisionChange,
    handleDateRangeTypeChange,
  };
}
